import { useEffect, useMemo, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";
import Loading from "@/components/Loading/Loading";

import styles from "./MarketplaceRouteShell.module.scss";

const ALLOWED_STATES = new Set([
  "activeClubMember",
  "activeClubHost",
  "activeClubPartner",
]);

const safeStr = (value) => (value == null ? "" : String(value));
const hasText = (value) => safeStr(value).trim().length > 0;

export default function MarketplaceRouteShell() {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}`);
        if (ignore) return;

        const memberState = safeStr(data?.memberState);
        if (!ALLOWED_STATES.has(memberState)) {
          navigate(`/lounge/clubs/single/${clubId}`, { replace: true });
          return;
        }

        setPayload(data || null);
      } catch (err) {
        if (ignore) return;
        setError(
          err?.response?.data?.message ||
            t("clubHome.states.error", "Something went wrong."),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, navigate, t]);

  const club = payload?.club || null;
  const memberState = safeStr(payload?.memberState);

  const clubName =
    safeStr(club?.clubName) || t("clubHome.header.clubName", "Club");

  const clubNo = club?.clubNo != null ? String(club.clubNo) : "";
  const streetName = safeStr(club?.streetName);
  const cityName = safeStr(club?.cityName);

  const clubAddressParts = [
    hasText(clubNo) ? `${t("clubHome.header.clubNo", "Club")} ${clubNo}` : "",
    streetName,
    cityName,
  ].filter((value) => hasText(value));

  const clubAddress = clubAddressParts.length
    ? clubAddressParts.join(" · ")
    : t("clubHome.header.addressFallback", "Address not available");

  const clubAvatarSrc =
    club?.clubAvatarThumb?.url || club?.clubAvatar?.url || avatarPng;

  const showHostDash = memberState === "activeClubHost";
  const showPartnerDash = memberState === "activeClubPartner";

  const dashMeta = useMemo(() => {
    if (showHostDash) {
      return {
        label: t("clubHome.header.hostDashboard", "Host Dashboard"),
        to: `/lounge/clubs/single/${clubId}/host-dashboard`,
      };
    }

    if (showPartnerDash) {
      return {
        label: t("clubHome.header.partnerDashboard", "Partner Dashboard"),
        to: `/lounge/clubs/single/${clubId}/partner-dashboard`,
      };
    }

    return null;
  }, [clubId, showHostDash, showPartnerDash, t]);

  const sellerDashboardMeta = useMemo(
    () => ({
      label: t("marketplace.sellerDashboard", "Seller Dashboard"),
      to: `/lounge/marketplace/${clubId}/seller`,
    }),
    [clubId, t],
  );

  const actionLinks = useMemo(
    () => [dashMeta, sellerDashboardMeta].filter(Boolean),
    [dashMeta, sellerDashboardMeta],
  );
  const isSellerPath = safeStr(location.pathname).startsWith(
    `/lounge/marketplace/${clubId}/seller`,
  );

  const clubBase = `/lounge/clubs/single/${clubId}`;
  const clubTabs = [
    {
      key: "feed",
      label: t("clubHome.tabs.feed", "Feed"),
      to: `${clubBase}/home`,
    },
    {
      key: "members",
      label: t("clubHome.tabs.members", "Members"),
      to: `${clubBase}/members`,
    },
    {
      key: "events",
      label: t("clubHome.tabs.events", "Events"),
      to: `${clubBase}/events`,
    },
    {
      key: "shop",
      label: t("clubHome.tabs.shop", "Shop"),
      to: `/lounge/marketplace/${clubId}`,
    },
  ];

  const isActiveTab = (to) => {
    const pathname = safeStr(location.pathname);
    if (pathname === to) return true;
    if (to.endsWith(`/marketplace/${clubId}`)) {
      if (isSellerPath) return false;
      return pathname.startsWith(`/lounge/marketplace/${clubId}`);
    }
    return pathname.startsWith(to);
  };

  if (loading && !payload && !error) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={styles.page}>
      {/* Tabs row (keep as-is) */}
      <nav
        className={styles.tabs}
        aria-label={t("clubHome.topTabs.aria", "Lounge")}
      >
        <Link
          to="/lounge"
          className={`${styles.tabButton} ${isPublic ? styles.activeTab : ""}`}
        >
          {t("clubHome.topTabs.public", "Public")}
        </Link>
        <Link
          to="/lounge/friends-feed"
          className={`${styles.tabButton} ${isFriends ? styles.activeTab : ""}`}
        >
          {t("clubHome.topTabs.friends", "Friends")}
        </Link>
        <Link
          to="/lounge/clubs-feed"
          className={`${styles.tabButton} ${isClubs ? styles.activeTab : ""}`}
        >
          {t("clubHome.topTabs.clubs", "Clubs")}
        </Link>
        <Link
          to="/lounge/chat"
          className={`${styles.tabButton} ${isChat ? styles.activeTab : ""}`}
        >
          {t("clubHome.topTabs.chat", "Chat")}
        </Link>
        <Link
          to="/lounge/search"
          className={`${styles.tabButton} ${isSearch ? styles.activeTab : ""}`}
        >
          🔍 {t("clubHome.topTabs.search", "Search")}
        </Link>
      </nav>

      {/* Header bar (avatar + name + actions) */}
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.avatarWrap}>
            <img className={styles.avatar} src={clubAvatarSrc} alt={clubName} />
            <span className={styles.badgeOnline}>
              {t("clubHome.header.active", "Active")}
            </span>
          </div>

          <div className={styles.headerInfo}>
            <div className={styles.displayName}>{clubName}</div>

            <div className={styles.usernameRow}>
              <span className={styles.memberSince}>{clubAddress}</span>
            </div>

            <div className={styles.metaRow}>
              {actionLinks.map((action) => (
                <Link
                  key={action.to}
                  className={`${styles.btnPrimary} ${
                    action.to === sellerDashboardMeta.to && isSellerPath
                      ? styles.btnPrimaryActive
                      : ""
                  }`}
                  to={action.to}
                >
                  {action.label}
                </Link>
              ))}
            </div>

            {loading ? (
              <div className={styles.stateRow}>
                <span className={styles.stateDot} />
                <span className={styles.stateText}>
                  {t("clubHome.states.loading", "Loading club...")}
                </span>
              </div>
            ) : null}

            {!loading && error ? (
              <div className={styles.stateRowError}>
                <span className={styles.stateDot} />
                <span className={styles.stateText}>{error}</span>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      {/* Tabs row (club sections) */}
      <nav
        className={styles.tabs}
        aria-label={t("clubHome.clubTabs.aria", "Club")}
      >
        {clubTabs.map((tab) => {
          const active = isActiveTab(tab.to);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`${styles.tabButton} ${active ? styles.activeTab : ""}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Center-only content (no left column) */}
      <div className={styles.centerCol}>
        <Outlet />
      </div>
    </div>
  );
}
